/**
 * @file This file contains the PlayerStatsManager class, responsible for handling
 * player-specific persistent data such as currency (gold).
 * @module PlayerStatsManager
 */

export class PlayerStatsManager {
    /**
     * The key used to store player stats in localStorage.
     * @type {string}
     */
    static STORAGE_KEY = 'mergeVillagePlayerStats';

    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {HTMLElement} dependencies.appContainer - The main application container element, used for dispatching custom events.
     * @param {import('./AudioManager.js').AudioManager} dependencies.audioManager - The audio manager instance.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The global event emitter.
     * @param {boolean} [dependencies.inMemory=false] - If true, stats are not saved to or loaded from localStorage.
     */
    constructor({ appContainer, audioManager, eventEmitter, inMemory = false }) {
        /** @private @type {HTMLElement} */
        this.appContainer = appContainer;
        this.audioManager = audioManager;
        this.eventEmitter = eventEmitter;
        /** @private */
        this.inMemory = inMemory;
        this.savingDisabled = false;
        /**
         * The player's current statistics.
         * @private
         * @type {object}
         * @property {number} gold - The amount of gold the player has.
         * @property {Set<string>} unlockedAchievements - A set of unlocked achievement keys.
         * @property {Object.<string, number>} achievementProgress - A map of achievement keys to their current count.
         * @property {number} totalMerges - The total number of merges performed by the player over their lifetime.
         * @property {number} totalDisastersSurvived - The total number of disasters survived by the player over their lifetime.
         * @property {Set<string>} seenTooltips - A set of tooltip IDs that have been shown.
         * @property {boolean} hasPlayedBefore - A flag to check if it's the user's first game.
         */
        this.stats = this.loadStats();
    }

    /**
     * Loads player statistics from localStorage.
     * @returns {object} The loaded player stats, or a default object if no stats are found.
     */
    loadStats() {
        if (this.inMemory) {
            return { 
                gold: 0, 
                unlockedAchievements: new Set(), 
                achievementProgress: {}, 
                totalMerges: 0, 
                totalDisastersSurvived: 0,
                seenTooltips: new Set(),
                hasPlayedBefore: false };
        }
        const statsJSON = localStorage.getItem(PlayerStatsManager.STORAGE_KEY);
        if (statsJSON) {
            const loaded = JSON.parse(statsJSON);
            // Convert achievements array back to a Set for efficient lookups
            return {
                gold: loaded.gold || 0,
                unlockedAchievements: new Set(loaded.achievements || []),
                achievementProgress: loaded.achievementProgress || {},
                totalMerges: loaded.totalMerges || 0, 
                totalDisastersSurvived: loaded.totalDisastersSurvived || 0,
                seenTooltips: new Set(loaded.seenTooltips || []),
                hasPlayedBefore: loaded.hasPlayedBefore || false
            };
        }
        return { 
            gold: 0, 
            unlockedAchievements: new Set(), 
            achievementProgress: {}, 
            totalMerges: 0, 
            totalDisastersSurvived: 0,
            seenTooltips: new Set(),
            hasPlayedBefore: false
        };
    }

    /**
     * Saves the current player statistics to localStorage.
     */
    saveStats() {
        if (this.inMemory || this.savingDisabled) {
            return;
        }
        // Convert Set to Array for JSON serialization
        const savableStats = {
            gold: this.stats.gold,
            achievements: Array.from(this.stats.unlockedAchievements),
            achievementProgress: this.stats.achievementProgress,
            totalMerges: this.stats.totalMerges,
            totalDisastersSurvived: this.stats.totalDisastersSurvived,
            seenTooltips: Array.from(this.stats.seenTooltips),
            hasPlayedBefore: this.stats.hasPlayedBefore
        };
        localStorage.setItem(PlayerStatsManager.STORAGE_KEY, JSON.stringify(savableStats));
    }

    /**
     * Gets the current amount of gold the player has.
     * @returns {number} The player's current gold amount.
     */
    getGold() {
        return this.stats.gold;
    }

    /**
     * Adds a specified amount of gold to the player's total.
     * Dispatches a 'goldchanged' custom event.
     * @param {number} amount - The amount of gold to add.
     */
    addGold(amount) {
        this.audioManager.play('gold');
        this.stats.gold += amount;
        this.saveStats();
        if (this.appContainer) {
            const event = new CustomEvent('goldchanged', { detail: { newGold: this.stats.gold } });
            this.appContainer.dispatchEvent(event);
            this.eventEmitter.emit('achievement:goldUpdated', { newGoldTotal: this.stats.gold });
        }
    }

    /**
     * Resets all player statistics to their default values and saves.
     * Dispatches events to notify the UI of the changes.
     */
    resetStats() {
        this.stats = {
            gold: 0,
            unlockedAchievements: new Set(),
            achievementProgress: {},
            totalMerges: 0,
            totalDisastersSurvived: 0,
            seenTooltips: new Set(),
            hasPlayedBefore: false
        };
        this.saveStats();

        if (this.appContainer) {
            const goldEvent = new CustomEvent('goldchanged', { detail: { newGold: 0 } });
            this.appContainer.dispatchEvent(goldEvent);
            this.appContainer.dispatchEvent(new CustomEvent('playerStatsChanged'));
        }
    }

    /**
     * Unlocks a specific achievement and saves the state.
     * @param {string} achievementKey - The key of the achievement to unlock.
     */
    unlockAchievement(achievementKey) {
        if (!this.isAchievementUnlocked(achievementKey)) {
            this.stats.unlockedAchievements.add(achievementKey);
            this.saveStats();
        }
    }

    /**
     * Checks if a specific achievement has been unlocked.
     * @param {string} achievementKey - The key of the achievement to check.
     * @returns {boolean} True if the achievement is unlocked.
     */
    isAchievementUnlocked(achievementKey) {
        return this.stats.unlockedAchievements.has(achievementKey);
    }

    /**
     * Gets the set of unlocked achievement keys.
     * @returns {Set<string>} The set of unlocked achievement keys.
     */
    getUnlockedAchievements() {
        return this.stats.unlockedAchievements;
    }

    /**
     * Gets the entire achievement progress object.
     * @returns {Object.<string, number>} The achievement progress object.
     */
    getAchievementProgressObject() {
        return this.stats.achievementProgress;
    }

    /**
     * Increments the progress for a given achievement and saves.
     * @param {string} achievementKey - The key of the achievement to increment.
     * @returns {number} The new count for the achievement.
     */
    incrementAchievementProgress(achievementKey) {
        const currentCount = this.stats.achievementProgress[achievementKey] || 0;
        const newCount = currentCount + 1;
        this.stats.achievementProgress[achievementKey] = newCount;
        this.saveStats();
        return newCount;
    }

    /**
     * Increments the total number of lifetime merges and saves the stats.
     * @param {number} [count=1] - The number of merges to add.
     */
    incrementTotalMerges(count = 1) {
        this.stats.totalMerges = (this.stats.totalMerges || 0) + count;
        this.saveStats();
    }

    /**
     * Gets the total number of lifetime merges.
     * @returns {number} The total number of lifetime merges.
     */
    getTotalMerges() {
        return this.stats.totalMerges || 0;
    }

    /**
     * Increments the total number of lifetime disasters survived and saves the stats.
     * @param {number} [count=1] - The number of disasters to add.
     */
    incrementTotalDisastersSurvived(count = 1) {
        this.stats.totalDisastersSurvived = (this.stats.totalDisastersSurvived || 0) + count;
        this.saveStats();
    }

    /**
     * Checks if this is the user's first time playing. If it is, it sets a flag.
     * @param {boolean} [consume=false] - If true, marks the first play as having occurred. Use markFirstPlayComplete() for better clarity.
     * @returns {boolean} True if it's the first play session.
     */
    isFirstPlay(consume = false) {
        if (!this.stats.hasPlayedBefore) {
            if (consume) {
                this.markFirstPlayComplete();
            }
            return true;
        }
        return false;
    }

    /**
     * Explicitly marks that the player has completed their first play-through/tutorial.
     */
    markFirstPlayComplete() {
        this.stats.hasPlayedBefore = true;
        this.saveStats();
    }

    /**
     * Checks if a tooltip has been seen by the user before.
     * @param {string} tooltipId - The ID of the tooltip.
     * @returns {boolean} True if the tooltip has been seen.
     */
    hasSeenTooltip(tooltipId) {
        return this.stats.seenTooltips.has(tooltipId);
    }

    setTooltipSeen(tooltipId) {
        this.stats.seenTooltips.add(tooltipId);
        this.saveStats();
    }
}